import React from 'react';
import { Checkbox, colors } from '@insurancemenu/imenu-components-lib';
import {
  getClassIcon,
  normalizeSubscriberClass,
  subscriberClassTypes,
} from 'global/utils/election/subscriberClassHelper';
import { EeClassesTypes, EeClass } from 'types/electionService';
import { observer } from 'mobx-react-lite';
import {
  SectionHeaderStyled,
  TypeNameStyled,
  ItemsWrapperStyled,
  ItemStyled,
  EmptyMessageStyled,
  SectionStyled,
} from './styles';

interface Props {
  classes: EeClass[];
  selectedClass: string;
  availableClasses?: string[];
  onSelect: (name: string) => void;
}

export type ClassUIType = 'selected' | 'default' | 'disabled';

const HIGHLIGHT_COLOR: Record<ClassUIType, string> = {
  default: colors.neutral[900],
  selected: colors.blue[600],
  disabled: colors.neutral[400],
};

export const ClassesList: React.FC<Props> = observer(({ classes, selectedClass, availableClasses = [], onSelect }) => {
  // When BE sends a non-empty availableClasses list, only classes in that list are enabled; all others are disabled.
  const isCheckboxEnabled = (currentClassName: string): boolean =>
    availableClasses.some((availableClassName) => {
      return normalizeSubscriberClass(availableClassName) === normalizeSubscriberClass(currentClassName);
    });

  // Determine the UI type of the checkbox based on the selected class and the availability of the class
  const getClassStatus = (currentClassName: string, isDisabled: boolean): ClassUIType => {
    if (isDisabled) return 'disabled';
    if (selectedClass === currentClassName) return 'selected';
    return 'default';
  };

  const sortAvailableFirst = (list: EeClass[]): EeClass[] => {
    if (availableClasses.length === 0) return list;
    return [...list].sort((a, b) => {
      const aAvailable = isCheckboxEnabled(a.name);
      const bAvailable = isCheckboxEnabled(b.name);
      if (aAvailable === bAvailable) return 0;
      return aAvailable ? -1 : 1;
    });
  };

  return (
    <>
      {subscriberClassTypes.map((type: EeClassesTypes) => {
        const classesOfType = sortAvailableFirst(classes.filter((eeClass) => eeClass.type === type));
        return (
          <SectionStyled key={type}>
            <SectionHeaderStyled>
              {getClassIcon(type)}
              <TypeNameStyled>{type.toLowerCase()}</TypeNameStyled>
            </SectionHeaderStyled>
            <ItemsWrapperStyled>
              {classesOfType.length === 0 ? (
                <EmptyMessageStyled>No classes added</EmptyMessageStyled>
              ) : (
                classesOfType.map(({ name }) => {
                  const classStatus = getClassStatus(
                    name,
                    availableClasses.length > 0 ? !isCheckboxEnabled(name) : false,
                  );
                  return (
                    <ItemStyled key={`${type}:${name}`}>
                      <Checkbox
                        label={name}
                        checked={classStatus === 'selected'}
                        disabled={classStatus === 'disabled'}
                        onChange={(): void => onSelect(name)}
                        highlightColor={HIGHLIGHT_COLOR[classStatus]}
                      />
                    </ItemStyled>
                  );
                })
              )}
            </ItemsWrapperStyled>
          </SectionStyled>
        );
      })}
    </>
  );
});
